// The TSIA2 calculator pop-up.
//
// Three calculators behind one toolbar button, offered cumulatively: an item
// flagged `graphing` also offers Square Root and Basic, one flagged `sqrt`
// offers Square Root and Basic, and one flagged `basic` opens the Basic
// calculator with no menu at all. That mirrors the real test, where the
// dropdown only appears when there is more than one choice.
//
// Basic and Square Root are the TI-108 the real test shows (reference
// screenshots 16 and 30), so a student who has practised here meets the same
// keys on test day: the same keys, and only those keys. The behaviour lives
// in ti108.ts as a pure state machine with its own tests; this file only
// renders it. Graphing is plain Desmos: a TI-84 is an emulator we cannot
// ship, and Desmos is a functional superset of what TSIA2 math asks for.
import React, { useEffect, useRef, useState } from 'react';

import {
    CalculatorKind, CalculatorMode, OFFERED, TI108Key, initialState, press,
} from './ti108';
import { useDraggable } from './useDraggable';

export type { CalculatorMode } from './ti108';

// Loaded from the CDN in public/index.html, as the SAT and ACT players use it.
declare const Desmos: any;

const KIND_LABELS: Record<CalculatorKind, string> = {
    basic: 'Basic',
    sqrt: 'Square Root',
    graphing: 'Graphing',
};

// --- the TI-108 ----------------------------------------------------------

// The keypad, row by row, as the real screen lays it out: a four-column
// grid, the square-root key (variant only) above M-, the equals key two rows
// tall. `null` is an empty cell.
type KeyCell = { key: TI108Key; red?: boolean; tall?: boolean; small?: boolean } | null;

const keypad = (withSquareRoot: boolean): KeyCell[][] => [
    [null, withSquareRoot ? { key: '√', red: true } : null, null, { key: '÷', red: true }],
    [{ key: 'MRC', red: true, small: true }, { key: 'M-', red: true, small: true }, { key: 'M+', red: true, small: true }, { key: '×', red: true }],
    [{ key: '7' }, { key: '8' }, { key: '9' }, { key: '-', red: true }],
    [{ key: '4' }, { key: '5' }, { key: '6' }, { key: '+', red: true }],
    [{ key: '1' }, { key: '2' }, { key: '3' }, { key: '=', red: true, tall: true }],
    [{ key: 'ON/C', red: true, small: true }, { key: '0' }, { key: '.' }, null],
];

// What the key shows. The model uses '-' so the tests read naturally; the
// real key is a minus sign.
const KEY_LABELS: Partial<Record<TI108Key, string>> = { '-': '−' };

function TI108({ withSquareRoot }: { withSquareRoot: boolean }) {
    const [state, setState] = useState(initialState);
    const tap = (key: TI108Key) => setState(current => press(current, key));

    return (
        <div className='tsia2-ti108'>
            <div className='tsia2-ti108-brand'>
                <span className='tsia2-ti108-maker'>Texas Instruments</span>
                <span className='tsia2-ti108-model'>TI-108</span>
            </div>
            <div className='tsia2-ti108-display'>
                <span className='tsia2-ti108-memory'>{state.memory !== 0 ? 'M' : ''}</span>
                <span className='tsia2-ti108-value'>
                    {/* The real display always ends in a point when the number has
                        none, the way an eight-digit LCD does. */}
                    {state.display === 'Error' || state.display.includes('.') ? state.display : `${state.display}.`}
                </span>
            </div>
            <div className='tsia2-ti108-keys'>
                {keypad(withSquareRoot).flatMap((row, r) => row.map((cell, c) => (
                    cell === null
                        // The cell under = is taken by its row span; the rest are
                        // real gaps in the keypad.
                        ? (r === 5 && c === 3 ? null : <span key={`${r}-${c}`} className='tsia2-key tsia2-key-blank' aria-hidden='true' />)
                        : (
                            <button
                                key={cell.key}
                                type='button'
                                className={'tsia2-key'
                                    + (cell.red ? ' tsia2-key-red' : '')
                                    + (cell.tall ? ' tsia2-key-tall' : '')
                                    + (cell.small ? ' tsia2-key-small' : '')}
                                onClick={() => tap(cell.key)}
                            >
                                {KEY_LABELS[cell.key] ?? cell.key}
                            </button>
                        )
                )))}
            </div>
        </div>
    );
}

// --- Desmos --------------------------------------------------------------

function GraphingCalculator() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current || typeof Desmos === 'undefined') return;
        const instance = Desmos.GraphingCalculator(ref.current, {
            notes: false,
            folders: false,
            images: false,
            links: false,
        });
        return () => instance.destroy();
    }, []);

    return <div ref={ref} className='tsia2-desmos' />;
}

// --- the panel -----------------------------------------------------------

export function TSIA2Calculator({ mode, onClose }: { mode: CalculatorMode; onClose: () => void }) {
    const offered = OFFERED[mode];
    const [kind, setKind] = useState<CalculatorKind>(offered[offered.length - 1]);
    // Shared with the review's explanation popup, so both pop-outs drag the
    // same way and there is one copy of the logic.
    const drag = useDraggable<HTMLDivElement>();

    return (
        <div
            ref={drag.ref}
            className={'tsia2-calculator' + (kind === 'graphing' ? ' is-graphing' : '')}
        >
            <div className='tsia2-calculator-header' onMouseDown={drag.onHeaderMouseDown}>
                <span>Calculator</span>
                <button type='button' className='tsia2-calculator-close' onClick={onClose}>×</button>
            </div>

            {/* No menu when only one calculator is offered -- the real test
                opens straight into it. */}
            {offered.length > 1 && (
                <div className='tsia2-calculator-tabs'>
                    {offered.map(option => (
                        <button
                            key={option}
                            type='button'
                            className={'tsia2-calculator-tab' + (option === kind ? ' is-selected' : '')}
                            onClick={() => setKind(option)}
                        >
                            {KIND_LABELS[option]}
                        </button>
                    ))}
                </div>
            )}

            <div className='tsia2-calculator-body'>
                {kind === 'graphing'
                    ? <GraphingCalculator />
                    : <TI108 key={kind} withSquareRoot={kind === 'sqrt'} />}
            </div>
        </div>
    );
}
